import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BookingModal from "../components/BookingModal";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Calendar, Clock, ArrowRight, Tag } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const { language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/articles`);
        setArticles(res.data);
      } catch (err) {
        console.error("Error fetching articles:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const langArticles = articles.filter((a) => (a.language || "en") === language);
  const visibleArticles = langArticles.length > 0 ? langArticles : articles.filter((a) => (a.language || "en") === "en");
  const isFr = language === "fr" && langArticles.length > 0;

  const categories = ["all", ...new Set(visibleArticles.map((a) => a.category).filter(Boolean))];

  const filteredArticles =
    selectedCategory === "all"
      ? visibleArticles
      : visibleArticles.filter((a) => a.category === selectedCategory);

  const readingTime = (content) => {
    const words = content?.replace(/<[^>]*>/g, "").split(/\s+/).length || 0;
    return Math.max(1, Math.ceil(words / 200));
  };

  return (
    <div className="min-h-screen bg-white">
      <Header onBookingClick={() => setBookingModalOpen(true)} />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-b from-sky-950 to-sky-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="blog-page-title">
            Blog & Guides
          </h1>
          <p className="text-lg text-sky-200 max-w-2xl mx-auto">
            {isFr
              ? "Conseils, guides et récits sur le kitesurf à Dakhla, Maroc"
              : "Tips, guides, and stories about kitesurfing in Dakhla, Morocco"}
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="border-b border-gray-200 bg-white sticky top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-4 overflow-x-auto">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat
                    ? "bg-sky-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`blog-cat-${cat}`}
              >
                {cat === "all" ? (isFr ? "Tous les articles" : "All Articles") : cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Loading articles...</p>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No articles yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <Link to={`/blog/${article.slug}`} key={article.id} data-testid={`blog-card-${article.slug}`}>
                  <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-1 h-full">
                    {article.featured_image && (
                      <div className="h-52 overflow-hidden">
                        <img
                          src={article.featured_image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        {article.category && (
                          <Badge variant="outline" className="text-xs capitalize">
                            {article.category}
                          </Badge>
                        )}
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {readingTime(article.content)} {isFr ? "min de lecture" : "min read"}
                        </span>
                      </div>
                      <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-sky-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {article.excerpt || article.content?.replace(/<[^>]*>/g, "").substring(0, 150)}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {article.published_at
                            ? new Date(article.published_at).toLocaleDateString(isFr ? "fr-FR" : "en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : "Draft"}
                        </span>
                        <span className="text-sky-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                          {isFr ? "Lire la suite" : "Read more"} <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <BookingModal isOpen={bookingModalOpen} onClose={() => setBookingModalOpen(false)} />
    </div>
  );
};

export default BlogPage;
